import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@navi-soft/ui/button";
import { TextField } from "@navi-soft/ui/text-field";
import {
  AuthCard,
  authErrorClassName,
  authFieldClassName,
  authFormClassName,
  authLinkClassName,
  authMutedClassName,
} from "@/components/auth-card";
import { forgotPassword } from "@/lib/api";

export const Route = createFileRoute("/forgot")({
  component: ForgotPage,
});

function ForgotPage() {
  const t = useTranslations("app");
  const [loginName, setLoginName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await forgotPassword(loginName);
      if (!res.ok) {
        setError(t("forgotFailed"));
        return;
      }
      setDone(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title={t("forgotTitle")}
      footer={
        <Link to="/login" className={authLinkClassName()}>
          {t("backToLogin")}
        </Link>
      }
    >
      {done ? (
        <p className={authMutedClassName()}>{t("forgotSent")}</p>
      ) : (
        <>
          <p className={authMutedClassName()}>{t("forgotHint")}</p>
          <form
            className={authFormClassName()}
            onSubmit={(e) => void onSubmit(e)}
          >
            <TextField
              size="lg"
              label={t("loginLabel")}
              autoComplete="username"
              value={loginName}
              onChange={(e) => setLoginName(e.target.value)}
              className={authFieldClassName()}
              required
              autoFocus
            />
            {error ? <p className={authErrorClassName()}>{error}</p> : null}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="mt-1 w-full"
              loading={busy}
              disabled={!loginName.trim()}
            >
              {t("forgotSubmit")}
            </Button>
          </form>
        </>
      )}
    </AuthCard>
  );
}
